import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';

const STEPS = {
    ANALYZING: 'analyzing',
    ANSWER: 'answer',
    GENERATING: 'generating',
    RESULTS: 'results',
    ERROR: 'error',
};

const AIWriteExperienceModal = ({ jobDescription, experiences, aiAnalysis, onInsert, onClose }) => {
    const [step, setStep] = useState(STEPS.ANALYZING);
    const [problems, setProblems] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [wantsAiRewrite, setWantsAiRewrite] = useState([]);           // null | 'yes' | 'no' per problem
    const [skipOverrides, setSkipOverrides] = useState([]);             // bool per problem — user overrides AI skip
    const [selectedKeywords, setSelectedKeywords] = useState(new Set());
    const [bullets, setBullets] = useState([]);
    const [selected, setSelected] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [retryStep, setRetryStep] = useState(null);
    const cachedProblems = useRef(null);
    const cachedJd = useRef(null);

    const analyzeProblems = useCallback(async () => {
        // Use cache if same job description
        if (cachedProblems.current && cachedJd.current === jobDescription) {
            const cached = cachedProblems.current;
            setProblems(cached);
            setAnswers(cached.map(() => ({ experiences: [{ story: '', metrics: '' }] })));
            setWantsAiRewrite(cached.map(() => null));
            setSkipOverrides(cached.map(() => false));
            setSelectedKeywords(new Set());
            setStep(STEPS.ANSWER);
            return;
        }

        setStep(STEPS.ANALYZING);
        setErrorMessage('');
        try {
            const existingBullets = experiences.map(e => e.bullets).filter(Boolean).join('\n');
            const res = await fetch('https://renaisons.com/api/analyze_problems.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    job_description: jobDescription,
                    existing_bullets: existingBullets,
                }),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Server error. Please try again.');
            const data = await res.json();
            if (data.status !== 'success') throw new Error(data.message || 'Failed to analyze job description.');
            cachedProblems.current = data.problems;
            cachedJd.current = jobDescription;
            setProblems(data.problems);
            setAnswers(data.problems.map(() => ({ experiences: [{ story: '', metrics: '' }] })));
            setWantsAiRewrite(data.problems.map(() => null));
            setSkipOverrides(data.problems.map(() => false));
            setSelectedKeywords(new Set());
            setStep(STEPS.ANSWER);
        } catch (err) {
            setErrorMessage(err.message);
            setRetryStep('analyze');
            setStep(STEPS.ERROR);
        }
    }, [jobDescription, experiences]);

    const generateBullets = useCallback(async () => {
        setStep(STEPS.GENERATING);
        setErrorMessage('');
        try {
            const allExistingBullets = experiences.map(e => e.bullets).filter(Boolean).join('\n');
            const kwList = [...selectedKeywords];

            const payload = [];
            problems.forEach((p, idx) => {
                const hasAiExp = p.ai_has_experience;
                // Skip problems with no experience unless user overrode
                if (!hasAiExp && !skipOverrides[idx]) return;

                // For detected experience: skip if user chose to keep bullets as-is
                if (hasAiExp) {
                    const rewrite = wantsAiRewrite[idx];
                    if (rewrite === 'no') return;
                    if (rewrite === null) return;
                }

                const isRewrite = hasAiExp && wantsAiRewrite[idx] === 'yes';
                const nonEmptyExps = answers[idx].experiences.filter((e, eIdx) => eIdx === 0 || e.story.trim().length > 0);

                nonEmptyExps.forEach(exp => {
                    payload.push({
                        title: p.title,
                        description: p.description,
                        story: exp.story,
                        ...(exp.metrics && { metrics: exp.metrics }),
                        mode: isRewrite ? 'rewrite' : 'write',
                        ...(kwList.length > 0 && { suggested_keywords: kwList }),
                        ...(isRewrite && { existing_bullets: allExistingBullets }),
                    });
                });
            });

            const res = await fetch('https://renaisons.com/api/write_experience_bullets.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ problems: payload }),
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Server error. Please try again.');
            const data = await res.json();
            if (data.status !== 'success') throw new Error(data.message || 'Failed to generate bullets.');
            setBullets(data.bullets);
            setSelected(data.bullets.map(() => true));
            setAssignments(data.bullets.map(() => ''));
            setStep(STEPS.RESULTS);
        } catch (err) {
            setErrorMessage(err.message);
            setRetryStep('generate');
            setStep(STEPS.ERROR);
        }
    }, [problems, answers, wantsAiRewrite, skipOverrides, experiences, selectedKeywords]);

    useEffect(() => {
        analyzeProblems();
    }, [analyzeProblems]);

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const allResolved = answers.length > 0 && problems.every((problem, i) => {
        if (!problem.ai_has_experience) return true;
        if (wantsAiRewrite[i] === null) return false;
        return true;
    });

    const hasAtLeastOneToGenerate = problems.some((p, i) => {
        if (!p.ai_has_experience) return skipOverrides[i];
        return wantsAiRewrite[i] === 'yes';
    });

    const allAnswersFilled = allResolved && hasAtLeastOneToGenerate;

    const canInsert = selected.some(Boolean) &&
        selected.every((sel, i) => !sel || assignments[i] !== '');

    const updateAnswer = (problemIndex, expIndex, field, value) => {
        setAnswers(prev => {
            const updated = [...prev];
            const exps = [...updated[problemIndex].experiences];
            exps[expIndex] = { ...exps[expIndex], [field]: value };
            updated[problemIndex] = { ...updated[problemIndex], experiences: exps };
            return updated;
        });
    };

    const addExperienceEntry = (problemIndex) => {
        setAnswers(prev => {
            const updated = [...prev];
            updated[problemIndex] = {
                ...updated[problemIndex],
                experiences: [...updated[problemIndex].experiences, { story: '', metrics: '' }],
            };
            return updated;
        });
    };

    const updateWantsRewrite = (index, value) => {
        setWantsAiRewrite(prev => {
            const updated = [...prev];
            updated[index] = value || null;
            return updated;
        });
    };

    const toggleSkipOverride = (index) => {
        setSkipOverrides(prev => {
            const updated = [...prev];
            updated[index] = !updated[index];
            return updated;
        });
    };

    const toggleKeyword = (keyword) => {
        setSelectedKeywords(prev => {
            const next = new Set(prev);
            if (next.has(keyword)) next.delete(keyword);
            else next.add(keyword);
            return next;
        });
    };

    const keywordChips = useMemo(() => {
        const seen = new Set();
        return [
            ...(aiAnalysis?.missingKeywords?.map(k => ({ text: k.keyword || k, type: 'missing' })) || []),
            ...(aiAnalysis?.predictedKeywords?.map(k => ({ text: k.keyword || k, type: 'predicted' })) || []),
        ].filter(({ text }) => {
            if (seen.has(text)) return false;
            seen.add(text);
            return true;
        });
    }, [aiAnalysis]);

    const toggleSelected = (index) => {
        setSelected(prev => {
            const updated = [...prev];
            updated[index] = !updated[index];
            return updated;
        });
    };

    const updateAssignment = (index, experienceId) => {
        setAssignments(prev => {
            const updated = [...prev];
            updated[index] = experienceId;
            return updated;
        });
    };

    const getExperienceLabel = (exp, i) => {
        if (exp.role && exp.company) return `${exp.role} @ ${exp.company}`;
        if (exp.role) return exp.role;
        if (exp.company) return exp.company;
        return `Experience ${i + 1}`;
    };

    const handleInsert = () => {
        const toInsert = [];
        bullets.forEach((b, i) => {
            if (selected[i]) {
                toInsert.push({ bullet: `• ${b.bullet}`, experienceId: assignments[i] });
            }
        });
        onInsert(toInsert);
        onClose();
    };

    // Two explicit boxes: situation/examples + KPIs/metrics
    const renderExperienceFields = (problemIndex) => {
        const exps = answers[problemIndex]?.experiences || [];
        const multiEntry = exps.length > 1;
        return (
            <div className="space-y-4">
                {exps.map((exp, expIdx) => (
                    <div key={expIdx} className="space-y-2">
                        {multiEntry && (
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                                Experience {expIdx + 1}
                            </p>
                        )}
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
                                What you did & the situation
                            </p>
                            <textarea
                                className="w-full bg-[#1e293b] border border-gray-600 rounded-md p-2 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
                                rows={3}
                                value={exp.story}
                                onChange={(e) => updateAnswer(problemIndex, expIdx, 'story', e.target.value)}
                                placeholder="Briefly describe the situation and what you did..."
                            />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
                                KPIs & Metrics
                            </p>
                            <input
                                type="text"
                                className="w-full bg-[#1e293b] border border-gray-600 rounded-md p-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                value={exp.metrics}
                                onChange={(e) => updateAnswer(problemIndex, expIdx, 'metrics', e.target.value)}
                                placeholder="e.g. reduced churn by 30%, saved $200K/year"
                            />
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={() => addExperienceEntry(problemIndex)}
                    className="text-xs text-blue-400 hover:text-blue-300 font-semibold"
                >
                    + Add another experience
                </button>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#1e293b] border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-700 sticky top-0 bg-[#1e293b] z-10">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-blue-400" />
                        <h2 className="text-lg font-bold text-white">AI Write</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    {step === STEPS.ANALYZING && (
                        <div className="flex flex-col items-center py-12 gap-4">
                            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <p className="text-gray-400">Identifying key business problems...</p>
                        </div>
                    )}

                    {step === STEPS.ANSWER && (
                        <div>
                            <p className="text-sm text-gray-400 mb-6">Here's what this role actually needs to solve:</p>

                            {/* Global keyword chip tray */}
                            {keywordChips.length > 0 && (
                                <div className="mb-6 bg-[#0f172a] border border-gray-700 rounded-lg p-4">
                                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">
                                        Select keywords to force-include in your bullets:
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {keywordChips.map(({ text, type }) => {
                                            const isActive = selectedKeywords.has(text);
                                            return (
                                                <button
                                                    key={text}
                                                    type="button"
                                                    onClick={() => toggleKeyword(text)}
                                                    className={`px-2 py-0.5 text-xs font-semibold rounded-full border cursor-pointer transition-all ${isActive
                                                            ? type === 'missing'
                                                                ? 'bg-red-600 text-white border-red-500'
                                                                : 'bg-purple-600 text-white border-purple-500'
                                                            : type === 'missing'
                                                                ? 'bg-red-950/30 text-red-300 border-red-900/50 hover:border-red-500'
                                                                : 'bg-purple-950/30 text-purple-300 border-purple-900/50 hover:border-purple-500'
                                                        }`}
                                                >
                                                    {isActive ? `✓ ${text}` : `+ ${text}`}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {selectedKeywords.size > 0 && (
                                        <p className="text-[10px] text-gray-500 mt-2">
                                            {selectedKeywords.size} keyword{selectedKeywords.size > 1 ? 's' : ''} selected — AI will choose to include {selectedKeywords.size > 1 ? 'them' : 'it'} if it fits naturally in the bullet.
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="space-y-6">
                                {problems.map((problem, i) => {
                                    const hasAiExp = problem.ai_has_experience;

                                    return (
                                        <div key={problem.id} className="bg-[#0f172a] border border-gray-700 rounded-lg p-4 space-y-3">
                                            <div>
                                                <p className="text-sm font-bold text-blue-400">{i + 1}. {problem.title}</p>
                                                <p className="text-xs text-gray-400 mt-1">{problem.description}</p>
                                            </div>

                                            {/* No experience detected */}
                                            {!hasAiExp && !skipOverrides[i] && (
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs text-gray-500 italic">No relevant experience detected.</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleSkipOverride(i)}
                                                        className="text-xs text-blue-400 hover:text-blue-300 font-semibold ml-4 flex-shrink-0"
                                                    >
                                                        Write a bullet for this anyway →
                                                    </button>
                                                </div>
                                            )}

                                            {/* No experience — user chose to write anyway */}
                                            {!hasAiExp && skipOverrides[i] && (
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Writing from scratch</p>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleSkipOverride(i)}
                                                            className="text-xs text-gray-500 hover:text-gray-400"
                                                        >
                                                            ✕ Cancel
                                                        </button>
                                                    </div>
                                                    {renderExperienceFields(i)}
                                                </div>
                                            )}

                                            {/* Experience detected */}
                                            {hasAiExp && (
                                                <div className="space-y-3">
                                                    <p className="text-xs text-green-400 font-semibold">Relevant experience detected</p>

                                                    <select
                                                        value={wantsAiRewrite[i] || ''}
                                                        onChange={(e) => updateWantsRewrite(i, e.target.value)}
                                                        className="w-full bg-[#1e293b] border border-gray-600 rounded-md p-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                                    >
                                                        <option value="">Want AI to rewrite your bullets with relevant keywords?</option>
                                                        <option value="yes">Yes, rewrite with keywords</option>
                                                        <option value="no">No, I'll keep my bullets as-is</option>
                                                    </select>

                                                    {wantsAiRewrite[i] === 'no' && (
                                                        <p className="text-xs text-gray-500 italic">Skipped — keeping your existing bullets.</p>
                                                    )}

                                                    {wantsAiRewrite[i] === 'yes' && renderExperienceFields(i)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex justify-end mt-6">
                                <button
                                    onClick={generateBullets}
                                    disabled={!allAnswersFilled}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-6 rounded-md text-sm"
                                >
                                    Generate Bullets
                                </button>
                            </div>
                        </div>
                    )}

                    {step === STEPS.GENERATING && (
                        <div className="flex flex-col items-center py-12 gap-4">
                            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <p className="text-gray-400">Writing your bullets...</p>
                        </div>
                    )}

                    {step === STEPS.RESULTS && (
                        <div>
                            {(() => {
                                const allKeywords = [...new Set([
                                    ...(aiAnalysis?.missingKeywords?.map(k => k.keyword || k) || []),
                                    ...(aiAnalysis?.predictedKeywords?.map(k => k.keyword || k) || []),
                                ])];
                                const allBulletText = bullets.map(b => b.bullet).join(' ');
                                const makeWordBoundaryRegex = (kw) => new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
                                const covered = allKeywords.filter(kw => makeWordBoundaryRegex(kw).test(allBulletText));
                                const stillMissing = allKeywords.filter(kw => !makeWordBoundaryRegex(kw).test(allBulletText));

                                return allKeywords.length > 0 ? (
                                    <div className="mb-4 bg-[#0f172a] border border-gray-700 rounded-lg p-4">
                                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-2">
                                            Keyword Coverage
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {covered.map(kw => (
                                                <span key={kw} className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-950/30 text-green-300 border border-green-900/50">
                                                    ✓ {kw}
                                                </span>
                                            ))}
                                            {stillMissing.map(kw => (
                                                <span key={kw} className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-950/30 text-red-300 border border-red-900/50">
                                                    ✗ {kw}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            <p className="text-sm text-gray-400 mb-4">Select the bullets you want to add and assign each to an experience:</p>
                            <div className="space-y-3">
                                {bullets.map((b, i) => (
                                    <div
                                        key={i}
                                        className="bg-[#0f172a] border border-gray-700 rounded-lg p-4 cursor-pointer"
                                        onClick={() => toggleSelected(i)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selected[i]}
                                                onChange={() => toggleSelected(i)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="mt-1 flex-shrink-0 accent-blue-500 cursor-pointer"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-blue-400 font-semibold mb-1">Re: {b.problem_title}</p>
                                                <p className="text-sm text-white mb-3">• {b.bullet}</p>
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <select
                                                        value={assignments[i]}
                                                        onChange={(e) => updateAssignment(i, e.target.value)}
                                                        disabled={!selected[i]}
                                                        className="w-full bg-[#1e293b] border border-gray-600 rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        <option value="">— Assign to experience —</option>
                                                        {experiences.map((exp, ei) => (
                                                            <option key={exp.id} value={exp.id}>
                                                                {getExperienceLabel(exp, ei)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {selected[i] && assignments[i] === '' && (
                                                        <p className="text-xs text-yellow-400 mt-1">Select an experience to enable insert</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end mt-6">
                                <button
                                    onClick={handleInsert}
                                    disabled={!canInsert}
                                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-6 rounded-md text-sm"
                                >
                                    Insert Selected Bullets
                                </button>
                            </div>
                        </div>
                    )}

                    {step === STEPS.ERROR && (
                        <div className="flex flex-col items-center py-12 gap-4 text-center">
                            <p className="text-red-400 font-semibold">{errorMessage}</p>
                            <button
                                onClick={() => retryStep === 'analyze' ? analyzeProblems() : generateBullets()}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md text-sm"
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AIWriteExperienceModal;
